import type { FC, ComponentProps } from 'react';
import React from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';

import { ByLine } from '@confluence/content-header/entry-points/ByLine.ts';
import { ContentTitle } from '@confluence/content-header/entry-points/ContentTitle.ts';
import { TitleAlignmentType } from '@confluence/content-topper/entry-points/contentTopperConstants.ts';
import { useTitleContentPropertiesForPublishedPage } from '@confluence/content-topper/entry-points/useTitleContentPropertiesForPublishedPage.ts';

import { useViewPageParams } from './useViewPageParams.ts';

type ByLinePassThroughProps = Pick<
	ComponentProps<typeof ByLine>,
	'hasByLineContributors' | 'hasByLineExtensions'
>;

type Props = Partial<ByLinePassThroughProps> &
	ComponentProps<typeof ContentTitle> & {
		hasCoverPicture?: boolean;
		pageWidthType?: string;
		isEmbeddedPage?: boolean;
		shouldShowBylineReactions?: boolean;
		contentType?: string | null;
		spaceId?: string | null;
		contentSubType?: string | null;
		hasByLine?: boolean;
	};

const BylineCenterStyles = xcss({
	justifyContent: 'center',
});

export const ContentTitleWithByLine: FC<Props> = ({
	contentId,
	spaceKey,
	banner,
	hasByLineContributors,
	hasByLineExtensions,
	headingContainer,
	hasCoverPicture,
	pageWidthType,
	isEmbeddedPage,
	shouldShowBylineReactions,
	contentType,
	contentSubType,
	spaceId,
	hasByLine = true,
}: Props) => {
	const { versionOverride, embeddedContentRender } = useViewPageParams();

	const { titleContentProperties } = useTitleContentPropertiesForPublishedPage({
		contentId,
	});

	const isBylineCenterStyles =
		titleContentProperties?.titleLayoutAlignment == TitleAlignmentType.CENTER;

	return (
		<ContentTitle
			banner={banner}
			contentId={contentId}
			spaceKey={spaceKey}
			headingContainer={headingContainer}
			hasCoverPicture={hasCoverPicture}
			pageWidthType={pageWidthType}
			isEmbeddedPage={isEmbeddedPage}
		>
			<Box
				xcss={[isBylineCenterStyles && BylineCenterStyles]}
				testId={
					isBylineCenterStyles ? 'centered-content-title-and-byline' : 'content-title-and-byline'
				}
			>
				{hasByLine && (
					<ByLine
						contentId={contentId}
						versionOverride={versionOverride}
						embeddedContentRender={embeddedContentRender}
						hasByLineContributors={hasByLineContributors}
						hasByLineExtensions={hasByLineExtensions}
						shouldShowBylineReactions={shouldShowBylineReactions}
						contentType={contentType}
						contentSubType={contentSubType}
						spaceId={spaceId}
					/>
				)}
			</Box>
		</ContentTitle>
	);
};
